import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, FileCheck2, Lock, Check, X, CheckCircle2,
  ChevronRight, BookOpen, Fingerprint, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/marketing/Reveal";
import SectionHeading from "@/components/marketing/SectionHeading";
import { useI18n } from "@/context/I18nContext";
import { getServices, getWallets, getWhy, getHowSteps, getSecurityPoints, getEligible, getNotSupported, getBlogCategories } from "@/data/site";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SEC_IMG = "https://images.unsplash.com/photo-1765408217678-545de3e8941d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const TRUST = [
  { icon: Fingerprint, key: "trust.identity" },
  { icon: FileCheck2, key: "trust.nda" },
  { icon: ShieldCheck, key: "trust.contract" },
  { icon: Lock, key: "trust.access" },
];

const HC = {
  de: {
    approfondisci: "Mehr erfahren", request: "Bewertung anfragen", more: "Weitere Formate →",
    eligibleTab: "Möglicherweise bewertbar", notTab: "Nicht unterstützt",
    services: {
      "recupero-password": { title: "Passwort-Wiederherstellung", short: "Unterstützung, wenn das Wallet-Passwort vergessen wurde, aber Hinweise oder Backups vorhanden sind.", cases: ["Teilweise erinnertes Passwort", "Bekannte Passwortvarianten", "Wallet-Datei-Backup vorhanden"] },
      "seed-phrase-incompleta": { title: "Rekonstruktion unvollständiger Seed-Phrasen", short: "Technische Analyse, wenn Wörter fehlen oder die Reihenfolge falsch ist.", cases: ["1–3 Wörter fehlen", "Falsche Wortreihenfolge", "Ungültiges Wort in der Phrase"] },
      "wallet-danneggiati": { title: "Wiederherstellung beschädigter oder Legacy-Wallets", short: "Hilfe bei beschädigten Wallet-Dateien und nicht mehr unterstützter Software.", cases: ["Beschädigte wallet.dat-Datei", "Veraltete Wallet-Software", "Nicht öffenbares verschlüsseltes Backup"] },
      "eredita-digitale": { title: "Digitaler Nachlass und professionelle Unterstützung", short: "Hilfe für Erben und Fachleute mit angemessener rechtlicher Dokumentation.", cases: ["Nachlass mit Dokumentation", "Rechtliches oder notarielles Mandat", "Unterstützung für Unternehmen"] },
    },
    why: [
      { title: "Vertraulichkeit und Sicherheit", text: "Kontrollierter Informationszugriff, Dokumentenverschlüsselung und Audit-Protokoll." },
      { title: "Individuelle technische Analyse", text: "Jeder Fall wird einzeln anhand der noch verfügbaren Unterlagen bewertet." },
      { title: "Transparenter Prozess", text: "Umfang, Autorisierung und Vergütung werden vor der Bearbeitung schriftlich festgelegt." },
      { title: "Direkte Kommunikation", text: "Klare Statusaktualisierungen über das geschützte Portal." },
    ],
    how: [
      { title: "Vertrauliche Bewertung", text: "Der Kunde beschreibt den Fall ohne Wallet-Geheimnisse. Wir erheben nur die für die Voranalyse nötigen Informationen." },
      { title: "Prüfung und technische Analyse", text: "Identität, Eigentum und technische Machbarkeit der Wiederherstellung werden anhand der verfügbaren Unterlagen geprüft." },
      { title: "Bearbeitung und Übergabe", text: "Nach schriftlicher Autorisierung wird der Fall kontrolliert bearbeitet. Bei wiederhergestelltem Zugriff unterstützen wir die Absicherung des Wallets." },
    ],
    security: ["Non-custodial-Ansatz", "Identitäts- und Eigentumsprüfung", "Kontrollierter Zugriff auf Fallinformationen", "Dokumentenverschlüsselung", "Audit-Protokoll", "Umfang und Autorisierung schriftlich", "Keine öffentliche Übermittlung privater Schlüssel oder vollständiger Seed-Phrasen"],
    eligible: ["Vergessenes Passwort mit Teilhinweisen", "Unvollständige Seed-Phrase", "Falsche Wortreihenfolge", "Ungültige Wiederherstellungsphrase", "Verschlüsseltes Wallet-Backup", "Beschädigte Wallet-Datei", "Legacy-Wallet-Software", "Altes Gerät oder Hardware-Wallet", "Digitaler Nachlass mit rechtlicher Dokumentation"],
    notSupported: ["Wallets, die nicht dem Antragsteller gehören", "Anfragen ohne Eigentumsnachweis", "Vollständig verlorene Seed-Phrase ohne Datei, Schlüssel, Backup oder Gerät", "Rückgängigmachen von Blockchain-Transaktionen", "\"Hack-back\"-Anfragen", "Zugriff auf Konten Dritter", "Garantierte Wiederherstellung gestohlener Gelder"],
  },
  it: {
    approfondisci: "Approfondisci", request: "Richiedi una valutazione", more: "Altri formati →",
    eligibleTab: "Potenzialmente valutabile", notTab: "Non supportato",
    services: {
      "recupero-password": { title: "Recupero password", short: "Assistenza quando la password del wallet è dimenticata ma esistono indizi o backup.", cases: ["Password parzialmente ricordata", "Variazioni note della password", "Backup del file wallet disponibile"] },
      "seed-phrase-incompleta": { title: "Ricostruzione di seed phrase incomplete", short: "Analisi tecnica quando mancano alcune parole o l'ordine non è corretto.", cases: ["Mancano 1-3 parole", "Ordine delle parole errato", "Parola non valida nella frase"] },
      "wallet-danneggiati": { title: "Recupero di wallet danneggiati o legacy", short: "Supporto per file wallet corrotti e software non più supportato.", cases: ["File wallet.dat corrotto", "Software wallet obsoleto", "Backup cifrato non apribile"] },
      "eredita-digitale": { title: "Eredità digitale e supporto professionale", short: "Assistenza per eredi e professionisti con adeguata documentazione legale.", cases: ["Successione con documentazione", "Mandato legale o notarile", "Supporto per aziende"] },
    },
    why: [
      { title: "Riservatezza e sicurezza", text: "Accesso controllato alle informazioni, cifratura dei documenti e registro di audit." },
      { title: "Analisi tecnica personalizzata", text: "Ogni caso è valutato singolarmente in base al materiale ancora disponibile." },
      { title: "Processo trasparente", text: "Ambito, autorizzazione e compenso definiti per iscritto prima della lavorazione." },
      { title: "Comunicazione diretta", text: "Aggiornamenti chiari sullo stato della pratica tramite il portale riservato." },
    ],
    how: [
      { title: "Valutazione riservata", text: "Il cliente descrive il caso senza fornire segreti del wallet. Raccogliamo solo le informazioni necessarie all'analisi preliminare." },
      { title: "Verifica e analisi tecnica", text: "Vengono verificate identità, titolarità e fattibilità tecnica del recupero in base al materiale disponibile." },
      { title: "Lavorazione e consegna", text: "Dopo autorizzazione e accordo scritti, il caso è gestito con processo controllato. Se l'accesso viene ripristinato, assistiamo il cliente nella messa in sicurezza del wallet." },
    ],
    security: ["Approccio non-custodial", "Verifica di identità e titolarità", "Accesso controllato alle informazioni del caso", "Cifratura dei documenti", "Registro di audit", "Ambito e autorizzazione per iscritto", "Nessun invio pubblico di chiavi private o seed phrase complete"],
    eligible: ["Password dimenticata con indizi parziali", "Seed phrase incompleta", "Ordine delle parole errato", "Frase di recupero non valida", "Backup del wallet cifrato", "File wallet corrotto", "Software di wallet legacy", "Vecchio dispositivo o hardware wallet", "Eredità digitale con documentazione legale"],
    notSupported: ["Wallet non appartenenti al richiedente", "Richieste senza prova di titolarità", "Seed phrase completamente persa senza file, chiave, backup o dispositivo", "Annullamento di transazioni blockchain", "Richieste di tipo \"hack back\"", "Accesso ad account di terzi", "Recupero garantito di fondi rubati"],
  },
  en: {
    approfondisci: "Learn more", request: "Request an assessment", more: "More formats →",
    eligibleTab: "Potentially assessable", notTab: "Not supported",
    services: {
      "recupero-password": { title: "Password recovery", short: "Assistance when the wallet password is forgotten but clues or backups exist.", cases: ["Partially remembered password", "Known password variations", "Wallet file backup available"] },
      "seed-phrase-incompleta": { title: "Incomplete seed phrase reconstruction", short: "Technical analysis when some words are missing or the order is wrong.", cases: ["1–3 words missing", "Wrong word order", "Invalid word in the phrase"] },
      "wallet-danneggiati": { title: "Recovery of damaged or legacy wallets", short: "Support for corrupted wallet files and no-longer-supported software.", cases: ["Corrupted wallet.dat file", "Obsolete wallet software", "Encrypted backup that won't open"] },
      "eredita-digitale": { title: "Digital inheritance and professional support", short: "Assistance for heirs and professionals with appropriate legal documentation.", cases: ["Succession with documentation", "Legal or notarial mandate", "Support for companies"] },
    },
    why: [
      { title: "Confidentiality and security", text: "Controlled information access, document encryption and audit log." },
      { title: "Tailored technical analysis", text: "Each case is assessed individually based on the material still available." },
      { title: "Transparent process", text: "Scope, authorization and fee defined in writing before work begins." },
      { title: "Direct communication", text: "Clear status updates through the secure portal." },
    ],
    how: [
      { title: "Confidential assessment", text: "The client describes the case without providing wallet secrets. We collect only the information needed for the preliminary analysis." },
      { title: "Verification and technical analysis", text: "Identity, ownership and technical feasibility of recovery are verified based on the available material." },
      { title: "Processing and delivery", text: "After written authorization, the case is handled with a controlled process. If access is restored, we assist the client in securing the wallet." },
    ],
    security: ["Non-custodial approach", "Identity and ownership verification", "Controlled access to case information", "Document encryption", "Audit log", "Scope and authorization in writing", "No public sending of private keys or full seed phrases"],
    eligible: ["Forgotten password with partial clues", "Incomplete seed phrase", "Wrong word order", "Invalid recovery phrase", "Encrypted wallet backup", "Corrupted wallet file", "Legacy wallet software", "Old device or hardware wallet", "Digital inheritance with legal documentation"],
    notSupported: ["Wallets not belonging to the applicant", "Requests without proof of ownership", "Fully lost seed phrase with no file, key, backup or device", "Reversing blockchain transactions", "\"Hack back\" requests", "Access to third-party accounts", "Guaranteed recovery of stolen funds"],
  },
};

export default function Home() {
  const { lang, t } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const C = HC[lang] || HC.de;
  const services = getServices(lang);
  const wallets = getWallets(lang);
  const why = getWhy(lang);
  const how = getHowSteps(lang);
  const security = getSecurityPoints(lang);
  const eligible = getEligible(lang);
  const notSupported = getNotSupported(lang);
  const blogCategories = getBlogCategories(lang);
  const [faqs, setFaqs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/content/faq").then(({ data }) => setFaqs(data.slice(0, 6))).catch(() => {});
    api.get("/content/reviews").then(({ data }) => setReviews(data)).catch(() => {});
    api.get("/content/blog", { params: { status: "published" } }).then(({ data }) => setBlog(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="rc-ambient">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 rc-grid-lines opacity-60" />
        <div
          className="absolute right-0 top-0 h-full w-1/2 opacity-25 hidden lg:block"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to left, black, transparent)", WebkitMaskImage: "linear-gradient(to left, black, transparent)" }}
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyanx/30 bg-cyanx/10 px-3 py-1 text-xs text-cyanx">
                <span className="h-1.5 w-1.5 rounded-full bg-cyanx animate-pulse" />
                {L("Analisi blockchain · Gestione del caso riservata", "Blockchain analysis · Confidential case management", "Blockchain-Analyse · Vertrauliches Fallmanagement")}
              </span>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]" data-testid="hero-title">
                {L("Trasformiamo le tracce digitali in un ", "Turning digital evidence into a ", "Aus digitalen Spuren wird ein ")}<span className="text-gold">{L("percorso concreto", "clear path forward", "klarer nächster Schritt")}</span>.
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
                {L(
                  "Hai subito una frode crypto o perso il controllo dei tuoi asset digitali? Recovers Crypto analizza le transazioni, organizza le prove e ti aiuta a comprendere le possibili azioni tecniche e legali applicabili al tuo caso.",
                  "If you have been affected by cryptocurrency fraud or lost control of digital assets, Recovers Crypto analyzes transaction activity, organizes available evidence and helps you understand the technical and legal options relevant to your case.",
                  "Wenn Sie von Kryptobetrug betroffen sind oder die Kontrolle über digitale Vermögenswerte verloren haben, analysiert Recovers Crypto Transaktionen, strukturiert vorhandene Nachweise und hilft Ihnen, die technischen und rechtlichen Möglichkeiten Ihres Falls zu verstehen."
                )}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-hover font-medium h-12 px-7" data-testid="hero-request-assessment">
                  <Link to="/avvia-recupero">{L("Richiedi una valutazione", "Request an Assessment", "Bewertung anfragen")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 h-12 px-7" data-testid="hero-how-it-works">
                  <Link to="/come-funziona">{L("Scopri come lavoriamo", "Explore Our Process", "Unser Vorgehen ansehen")}</Link>
                </Button>
              </div>
              <p className="mt-6 text-sm text-slate-400">
                {L("Analisi blockchain · Tracciamento degli asset · Gestione del caso · Coordinamento legale", "Blockchain analysis · Asset tracing · Case management · Legal coordination", "Blockchain-Analyse · Asset-Tracing · Fallmanagement · Rechtliche Koordination")}
              </p>
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                {[
                  { icon: CheckCircle2, t: L("Valutazione preliminare", "Preliminary assessment", "Vorläufige Bewertung") },
                  { icon: Lock, t: L("Processo riservato e documentato", "Confidential, documented process", "Vertraulicher, dokumentierter Prozess") },
                  { icon: ShieldCheck, t: L("Nessuna garanzia di recupero", "No guaranteed recovery", "Keine garantierte Wiederherstellung") },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <p.icon className="h-5 w-5 text-cyanx shrink-0" />
                    {p.t}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-y border-white/10 bg-navy-paper/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST.map((item, i) => (
              <Reveal key={i} delay={i * 0.08} className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-cyanx">
                  <item.icon className="h-5 w-5" />
                </span>
                <span className="text-sm text-slate-300">{t(item.key)}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EURLA legal */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16">
        <div className="rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/5 to-transparent p-8 sm:p-10" data-testid="eurla-section">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
            <div className="max-w-2xl">
              <h3 className="font-heading text-2xl text-white">{L("Quando il caso richiede una valutazione legale", "When your case requires legal assessment", "Wenn Ihr Fall eine rechtliche Bewertung erfordert")}</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">
                {L(
                  "Alcuni casi relativi agli asset digitali possono richiedere competenze legali specifiche. Recovers Crypto organizza le informazioni tecniche e la documentazione disponibile, mentre il cliente può consultare EURLA per conoscere i professionisti e i servizi legali presentati sul relativo sito.",
                  "Certain digital-asset matters may require specific legal expertise. Recovers Crypto helps organize the available technical information and case documentation, while clients can visit EURLA to learn more about the legal professionals and services presented on its website.",
                  "Bestimmte Fälle im Zusammenhang mit digitalen Vermögenswerten können besondere rechtliche Fachkenntnisse erfordern. Recovers Crypto unterstützt bei der strukturierten Aufbereitung technischer Informationen und Falldokumente. Über EURLA können Kunden mehr über die dort vorgestellten Rechtsexperten und Dienstleistungen erfahren."
                )}
              </p>
            </div>
            <a href="https://eurla.eu/" target="_blank" rel="noopener noreferrer" data-testid="eurla-link-home" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-6 h-12 font-medium text-navy hover:bg-gold-hover transition-colors">
              {L("Consulta EURLA e i professionisti legali", "Explore EURLA Legal Professionals", "EURLA Rechtsexperten ansehen")} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <SectionHeading eyebrow={L("Servizi", "Services", "Dienstleistungen")} title={L("Come possiamo aiutarti", "How we can help you", "Wie wir Ihnen helfen können")} subtitle={L("Ogni caso è valutato individualmente in base al tipo di wallet e al materiale ancora disponibile.", "Each case is assessed individually based on the wallet type and the material still available.", "Jeder Fall wird individuell anhand des Wallet-Typs und der noch verfügbaren Unterlagen bewertet.")} />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => {
            return (
            <Reveal key={s.slug} delay={i * 0.06}>
              <div className="group h-full rounded-xl border border-white/10 bg-navy-paper p-7 hover:border-cyanx/30 transition-colors duration-300 shadow-lg shadow-black/20">
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gold/10 text-gold border border-gold/20">
                    <s.icon className="h-6 w-6" />
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-xl font-medium text-white">{s.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{s.short}</p>
                <ul className="mt-4 space-y-1.5">
                  {(s.cases || []).map((c) => (
                    <li key={c} className="flex items-center gap-2 text-xs text-slate-500">
                      <Check className="h-3.5 w-3.5 text-cyanx" />{c}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center gap-4">
                  <Link to={`/servizi/${s.slug}`} className="text-sm text-cyanx hover:text-white transition-colors inline-flex items-center gap-1" data-testid={`service-link-${s.slug}`}>
                    {C.approfondisci} <ChevronRight className="h-4 w-4" />
                  </Link>
                  <Link to="/avvia-recupero" className="text-sm text-gold hover:text-gold-hover transition-colors">{C.request}</Link>
                </div>
              </div>
            </Reveal>
          ); })}
        </div>
      </section>

      {/* WALLETS */}
      <section className="border-y border-white/10 bg-navy-paper/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <SectionHeading eyebrow={L("Wallet supportati", "Supported wallets", "Unterstützte Wallets")} title={L("Categorie di wallet che analizziamo", "Wallet categories we analyze", "Wallet-Kategorien, die wir analysieren")} subtitle={L("La fattibilità dipende dal tipo di wallet e dal materiale ancora disponibile.", "Feasibility depends on the wallet type and the material still available.", "Die Machbarkeit hängt vom Wallet-Typ und den noch verfügbaren Unterlagen ab.")} />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {wallets.map((w, i) => (
              <Reveal key={w.slug} delay={i * 0.03}>
                <Link to={`/wallet/${w.slug}`} data-testid={`wallet-card-${w.slug}`} className="block rounded-lg border border-white/10 bg-navy p-5 hover:border-cyanx/30 hover:bg-white/5 transition-all">
                  <div className="font-heading text-white font-medium">{w.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{w.type}</div>
                </Link>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link to="/wallet" className="flex h-full items-center justify-center rounded-lg border border-dashed border-white/15 p-5 text-sm text-slate-400 hover:text-white hover:border-cyanx/30 transition-colors">
                {C.more}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <SectionHeading eyebrow={L("Perché Recovers Crypto", "Why Recovers Crypto", "Warum Recovers Crypto")} title={L("Un approccio professionale e verificabile", "A professional and verifiable approach", "Ein professioneller und überprüfbarer Ansatz")} />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {why.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06}>
              <div className="h-full rounded-xl border border-white/10 bg-navy-paper p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-cyanx/10 text-cyanx border border-cyanx/20">
                  {w.icon && React.createElement(w.icon, { className: "h-5 w-5" })}
                </span>
                <h3 className="mt-4 font-heading text-lg font-medium text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="border-y border-white/10 bg-navy-paper/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <SectionHeading eyebrow={L("Come funziona", "How it works", "So funktioniert es")} title={L("Un processo in tre passaggi", "A three-step process", "Ein dreistufiger Prozess")} />
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {how.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative h-full rounded-xl border border-white/10 bg-navy p-7">
                  <span className="font-heading text-5xl font-light text-gold/30">{s.n || `0${i + 1}`}</span>
                  <h3 className="mt-3 font-heading text-xl font-medium text-white">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <SectionHeading eyebrow={L("Sicurezza", "Security", "Sicherheit")} title={L("Protezione e riservatezza in ogni fase", "Protection and confidentiality at every stage", "Schutz und Vertraulichkeit in jeder Phase")}
            subtitle={L("Adottiamo un approccio non-custodial: non chiediamo mai seed phrase complete, chiavi private o PIN.", "We take a non-custodial approach: we never ask for full seed phrases, private keys or PINs.", "Wir verfolgen einen Non-custodial-Ansatz: Wir fragen niemals nach vollständigen Seed-Phrasen, privaten Schlüsseln oder PINs.")} />
          <Reveal delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={SEC_IMG} alt={L("Sicurezza e cifratura", "Security and encryption", "Sicherheit und Verschlüsselung")} loading="lazy" className="w-full h-64 object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            </div>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {security.map((p, i) => (
            <Reveal key={p} delay={i * 0.04} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-navy-paper px-4 py-3">
              <ShieldCheck className="h-4 w-4 text-cyanx shrink-0" />
              <span className="text-sm text-slate-300">{p}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="border-y border-white/10 bg-navy-paper/40">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-24">
          <SectionHeading align="center" eyebrow={L("Idoneità del caso", "Case eligibility", "Fall-Eignung")} title={L("Il tuo caso è valutabile?", "Can your case be assessed?", "Kann Ihr Fall bewertet werden?")} className="mb-12" />
          <Tabs defaultValue="eligible" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-navy border border-white/10">
              <TabsTrigger value="eligible" data-testid="tab-eligible">{C.eligibleTab}</TabsTrigger>
              <TabsTrigger value="not" data-testid="tab-not-supported">{C.notTab}</TabsTrigger>
            </TabsList>
            <TabsContent value="eligible" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {eligible.map((e) => (
                <div key={e} className="flex items-center gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-slate-200">
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" />{e}
                </div>
              ))}
            </TabsContent>
            <TabsContent value="not" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {notSupported.map((e) => (
                <div key={e} className="flex items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-slate-200">
                  <X className="h-4 w-4 text-red-400 shrink-0" />{e}
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <SectionHeading eyebrow={L("Recensioni", "Reviews", "Bewertungen")} title={L("Testimonianze verificate", "Verified testimonials", "Verifizierte Erfahrungsberichte")}
          subtitle={L("Pubblichiamo solo testimonianze verificate inserite da un amministratore.", "We only publish verified testimonials added by an administrator.", "Wir veröffentlichen nur verifizierte, von einem Administrator hinzugefügte Erfahrungsberichte.")} />
        <div className="mt-12">
          {reviews.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-navy-paper p-10 text-center" data-testid="reviews-empty">
              <BookOpen className="mx-auto h-8 w-8 text-slate-600" />
              <p className="mt-3 text-sm text-slate-400">{L("Nessuna testimonianza pubblicata. I contenuti dimostrativi vengono mostrati solo quando approvati.", "No testimonials published. Demo content is shown only once approved.", "Keine Erfahrungsberichte veröffentlicht. Demo-Inhalte werden erst nach Freigabe angezeigt.")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.slice(0, 3).map((r) => (
                <div key={r.id} className="rounded-xl border border-white/10 bg-navy-paper p-6">
                  <p className="text-sm text-slate-300 leading-relaxed">"{r.body}"</p>
                  <p className="mt-4 text-xs text-slate-500">— {r.author_label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BLOG */}
      <section className="border-y border-white/10 bg-navy-paper/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <SectionHeading eyebrow={L("Risorse", "Resources", "Ressourcen")} title={L("Blog e knowledge base", "Blog and knowledge base", "Blog und Wissensdatenbank")} />
          <div className="mt-8 flex flex-wrap gap-2">
            {blogCategories.map((c) => (
              <span key={c} className="rounded-full border border-white/10 bg-navy px-3 py-1 text-xs text-slate-400">{c}</span>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {(blog.length ? blog : [null, null, null]).map((p, i) => (
              p ? (
                <Link key={p.id} to={`/blog/${p.slug}`} className="rounded-xl border border-white/10 bg-navy p-6 hover:border-cyanx/30 transition-colors" data-testid={`blog-card-${p.slug}`}>
                  <span className="text-xs text-cyanx">{p.category}</span>
                  <h3 className="mt-2 font-heading text-lg font-medium text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-3">{p.excerpt}</p>
                </Link>
              ) : (
                <div key={i} className="rounded-xl border border-dashed border-white/10 bg-navy p-6 text-sm text-slate-500">
                  {L("Articoli in preparazione.", "Articles in preparation.", "Artikel in Vorbereitung.")}
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-24">
        <SectionHeading align="center" eyebrow={L("FAQ", "FAQ", "FAQ")} title={L("Domande frequenti", "Frequently asked questions", "Häufig gestellte Fragen")} className="mb-12" />
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f) => (
            <AccordionItem key={f.id} value={f.id} className="border-white/10">
              <AccordionTrigger className="text-left text-white hover:text-cyanx" data-testid={`faq-${f.id}`}>{f.question}</AccordionTrigger>
              <AccordionContent className="text-slate-400 leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 text-center">
          <Link to="/faq" className="text-sm text-cyanx hover:text-white">{L("Vedi tutte le FAQ", "See all FAQs", "Alle FAQ ansehen")} →</Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-gold/20 bg-gradient-to-br from-navy-elevated to-navy-paper p-10 sm:p-14 text-center">
            <div className="absolute inset-0 rc-grid-lines opacity-40" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white">{L("Non riesci più ad accedere al tuo wallet?", "Can no longer access your wallet?", "Kein Zugriff mehr auf Ihr Wallet?")}</h2>
              <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
                {L("Invia le informazioni iniziali per ricevere una valutazione riservata della fattibilità tecnica.", "Send the initial information to receive a confidential assessment of technical feasibility.", "Senden Sie die ersten Informationen für eine vertrauliche Bewertung der technischen Machbarkeit.")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-hover font-medium h-12 px-7" data-testid="cta-start-case">
                  <Link to="/avvia-recupero">{L("Avvia una pratica", "Start a case", "Fall eröffnen")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 h-12 px-7" data-testid="cta-contact">
                  <Link to="/contatti">{L("Contatta il team", "Contact the team", "Team kontaktieren")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
